import React, { useEffect, useState } from "react";
import {
  AuditSettings,
  fetchAuditSettings,
  saveAuditSettings,
} from "../../../../services/visibilityService";

// The AI visibility check-up switch in the Visibility Manager's header (SGL-507).
// Off by default. The first free check-ups run without it; after that a
// check-up runs every 15 days only while it is on, and costs real credits.
const TERMS_URL = "https://sagelyn.ai/terms-of-service";

type Props = { customerId: string; idToken: string };

export const AuditCheckupToggle: React.FC<Props> = ({ customerId, idToken }) => {
  const [settings, setSettings] = useState<AuditSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tipOpen, setTipOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSettings(null);
    setError(null);
    fetchAuditSettings(customerId, idToken)
      .then((s) => !cancelled && setSettings(s))
      .catch(() => !cancelled && setError("Couldn't load check-up setting"));
    return () => {
      cancelled = true;
    };
  }, [customerId, idToken]);

  const toggle = async () => {
    if (!settings || saving) return;
    setSaving(true);
    setError(null);
    try {
      setSettings(await saveAuditSettings(customerId, !settings.enabled, idToken));
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!settings && !error) return null;

  const price = settings?.price_estimate ?? "1.20";
  const enabled = settings?.enabled ?? false;
  const freeLeft = settings?.free_runs_left ?? 0;

  return (
    <div
      className="relative flex items-center gap-1.5"
      onMouseEnter={() => setTipOpen(true)}
      onMouseLeave={() => setTipOpen(false)}
    >
      <span className="hidden text-[13px] font-medium text-theme-fg sm:inline">Check-up</span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label="AI visibility check-up every 15 days"
        disabled={!settings || saving}
        onClick={toggle}
        onFocus={() => setTipOpen(true)}
        onBlur={() => setTipOpen(false)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-theme-primary/40 disabled:opacity-50 ${
          enabled ? "bg-theme-primary" : "bg-theme-border"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            enabled ? "translate-x-[18px]" : "translate-x-0.5"
          }`}
        />
      </button>
      {error && <span className="text-[11px] font-medium text-red-600">{error}</span>}

      {tipOpen && (
        <div
          role="tooltip"
          className="absolute right-0 top-[calc(100%+8px)] z-[200] w-[280px] max-w-[calc(100vw-32px)] rounded-xl border border-theme-border bg-theme-surface p-3 text-[12px] leading-snug text-theme-fg shadow-2xl"
        >
          <p>
            Every 15 days your AI Visibility Manager checks how you show up in ChatGPT, Gemini and
            Perplexity and sends your 5 biggest fixes. About ${price} in credits per check. Reviewed
            by Claude Fable, which AWS may keep for 30 days for safety review (see{" "}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline">
              Terms
            </a>
            ).
          </p>
          {freeLeft > 0 && (
            <p className="mt-2 text-theme-fg-muted">
              {freeLeft} free check-up{freeLeft === 1 ? "" : "s"} left before this setting applies.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
